'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getRentValue, type Building } from '@repo/api';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RentValueSection } from './RentValueSection';
import { RentValueSectionSkeleton } from './RentValueSectionSkeleton';

interface RentValueProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function RentValue({ building, buildingAddress, showSkeleton, isReportReady }: RentValueProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [loading, setLoading] = useState(true);
  const [rentValueData, setRentValueData] = useState<any | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_rentvalue_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setLoading(false);
      setRentValueData(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getRentValue({ buildingId: building.id });
        setRentValueData(response);
      } catch (err) {
        console.error('[report][rent-value] failed to load', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isReportReady, building.id]);

  const shouldShowSkeleton = showSkeleton || loading;
  if (shouldShowSkeleton) return <RentValueSectionSkeleton />;

  return (
    <div ref={sectionRef}>
      <RentValueSection
        buildingAddress={buildingAddress}
        neighborhoodName={building.neighborhood?.name}
        boroughName={building.neighborhood?.borough}
        isBlurRequired={!isReportReady}
        rentValueData={rentValueData}
      />
    </div>
  );
}

export default RentValue;
